// src/components/Wizard/StimulusStep.jsx
import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { aiService } from '../../services/aiService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { edgeFunctionService } from '../../services/edgeFunctionService'

const { FiUpload, FiCheck, FiAlertTriangle } = FiIcons

const STIMULUS_BUCKET = 'stimulus'

const StimulusStep = ({ onComplete }) => {
  const { user } = useAuth()
  const location = useLocation()

  const [formData, setFormData] = useState({
    subject: '',
    content: '',
  })
  const [stimulusMode, setStimulusMode] = useState('text') // 'text' or 'file'
  const [uploadedFile, setUploadedFile] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  const [selectedThemes, setSelectedThemes] = useState([])
  const [suggestedThemes, setSuggestedThemes] = useState([])
  const [loading, setLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState('input') // 'input' or 'themes'

  // Track saved stimulus + extracted text (for both new + reused stimuli)
  const [savedStimulus, setSavedStimulus] = useState(null)
  const [extractedText, setExtractedText] = useState('')
  const [savingAndExtracting, setSavingAndExtracting] = useState(false)

  // 🔹 On mount: check for ?stimulusId=... and preload that stimulus for reuse
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stimulusId = params.get('stimulusId')
    if (!stimulusId) return

    const loadStimulus = async () => {
      try {
        setError('')
        console.log('Loading existing stimulus for reuse:', stimulusId)

        const { data, error: stimulusError } = await supabase
          .from('stimuli')
          .select('*')
          .eq('id', stimulusId)
          .single()

        if (stimulusError || !stimulusError && !data) {
          console.error('Error loading stimulus for reuse:', stimulusError)
          setError('Could not load the selected stimulus. Please try again.')
          return
        }

        const stimulus = data
        console.log('Loaded stimulus for reuse:', stimulus)

        setSavedStimulus(stimulus)
        setStimulusMode(
          stimulus.source_type === 'uploaded_file' ? 'file' : 'text',
        )

        // Pre-fill content for pasted-text stimuli (or where content exists)
        setFormData((prev) => ({
          ...prev,
          content: stimulus.content || prev.content,
        }))

        // Pre-fill extracted text (preferred), falling back to content
        const extracted =
          (stimulus.extracted_text ?? '').toString().trim() ||
          (stimulus.content ?? '').toString().trim()

        setExtractedText(extracted)
      } catch (err) {
        console.error('Unexpected error loading stimulus for reuse:', err)
        setError('Failed to load the selected stimulus. Please try again.')
      }
    }

    loadStimulus()
  }, [location.search])

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    setError('') // Clear error on input
  }

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setUploadedFile(file)
    setError('')
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const file = event.dataTransfer.files?.[0]
    if (!file) return
    setUploadedFile(file)
    setError('')
  }

  /**
   * Shared helper: create the stimulus row (and upload the file if present)
   * if it doesn't already exist, then return the stimulus.
   */
  const createOrUpdateStimulus = async () => {
    const isFileStimulus =
      (savedStimulus?.source_type === 'uploaded_file') || Boolean(uploadedFile)

    // If we already have a saved stimulus (reused or newly created), reuse it.
    if (savedStimulus) {
      console.log('Using existing savedStimulus:', savedStimulus.id)
      return savedStimulus
    }

    const baseStimulusInsert = {
      title: 'Untitled stimulus',
      description: null,
      content: formData.content, // may be empty for pure file-based flows
      source_type: isFileStimulus ? 'uploaded_file' : 'pasted_text',
      storage_bucket: isFileStimulus ? STIMULUS_BUCKET : null,
      storage_object_path: null,
      original_filename: isFileStimulus ? uploadedFile?.name ?? null : null,
      mime_type: isFileStimulus ? uploadedFile?.type ?? null : null,
      file_size_bytes: isFileStimulus ? uploadedFile?.size ?? null : null,
      created_by: user?.id ?? null,
    }

    const { data: stimulus, error: stimulusError } = await supabase
      .from('stimuli')
      .insert(baseStimulusInsert)
      .select()
      .single()

    if (stimulusError) {
      console.error('Error creating stimulus:', stimulusError)
      throw stimulusError
    }

    // If there's an uploaded file, upload it to Storage and update the row
    if (isFileStimulus && uploadedFile) {
      const ext =
        uploadedFile.name.split('.').pop()?.toLowerCase() || 'bin'
      const objectPath = `stimulus/${stimulus.id}/original.${ext}`

      const { error: uploadError } = await supabase.storage
        .from(STIMULUS_BUCKET)
        .upload(objectPath, uploadedFile, {
          upsert: true,
          contentType: uploadedFile.type || undefined,
        })

      if (uploadError) {
        console.error('Error uploading stimulus file:', uploadError)
        throw uploadError
      }

      const { error: updateError } = await supabase
        .from('stimuli')
        .update({ storage_object_path: objectPath })
        .eq('id', stimulus.id)

      if (updateError) {
        console.error(
          'Error updating stimulus with storage path:',
          updateError,
        )
        throw updateError
      }

      stimulus.storage_object_path = objectPath
    }

    setSavedStimulus(stimulus)
    return stimulus
  }

  /**
   * Save & extract button handler
   * - Creates the stimulus row
   * - Uploads file if present
   * - Calls extract_stimulus_text edge function
   * - Stores and shows extracted_text for debugging
   */
  const handleSaveAndExtract = async () => {
    setError('')

    if (!formData.subject.trim()) {
      setError('Please select a subject before saving.')
      return
    }

    const isFileStimulus =
      (savedStimulus?.source_type === 'uploaded_file') || Boolean(uploadedFile)

    // Require *some* content (either file or text) only if we don't already have a saved stimulus
    if (!savedStimulus && !isFileStimulus && !formData.content.trim()) {
      setError(
        'Please either paste some stimulus text or upload a file before saving.',
      )
      return
    }

    setSavingAndExtracting(true)
    try {
      console.log('=== SAVE & EXTRACT: creating/updating stimulus ===')
      const stimulus = await createOrUpdateStimulus()
      console.log('Stimulus saved for extraction:', stimulus)

      if (stimulus.source_type === 'uploaded_file') {
        console.log(
          'Calling extractStimulusText edge function for stimulus:',
          stimulus.id,
        )

        const result = await edgeFunctionService.extractStimulusText(
          stimulus.id,
        )
        console.log('extractStimulusText result:', result)

        // Try a few common shapes: {extracted_text}, {extractedText}, {stimulus: {extracted_text}}
        const extracted =
          result?.extracted_text ||
          result?.extractedText ||
          result?.stimulus?.extracted_text ||
          ''

        if (!extracted) {
          setError(
            'Extraction completed but no text was returned. Check the extract_stimulus_text logs.',
          )
        }

        setExtractedText(extracted)
      } else {
        // For pasted-text-only stimuli, "extracted" just means "content"
        setExtractedText(stimulus.content || '')
      }
    } catch (err) {
      console.error('Error in Save & Extract:', err)
      setError(
        err?.message ||
          'Failed to save and extract stimulus. Please try again.',
      )
    } finally {
      setSavingAndExtracting(false)
    }
  }

  const handleAnalyzeStimulus = async (e) => {
  e.preventDefault()
  setError('')

  if (!formData.subject.trim()) {
    setError('Please select a subject.')
    return
  }

  const hasSavedFileStimulus =
    savedStimulus?.source_type === 'uploaded_file'
  const hasExtractedText =
    extractedText && extractedText.trim().length >= 50

  // 🔹 Path 1: ALWAYS prefer extracted_text for a saved file-based stimulus
  if (hasSavedFileStimulus && hasExtractedText) {
    setAnalyzing(true)
    try {
      console.log('=== STARTING AI ANALYSIS (by stimulusId) ===')
      console.log('Subject:', formData.subject)
      console.log('StimulusId:', savedStimulus.id)
      console.log('Extracted text length:', extractedText.length)

      const themes = await aiService.analyzeStimulusByStimulusId(
        savedStimulus.id,
        formData.subject,
      )

      console.log('=== ANALYSIS COMPLETE ===')
      console.log('Number of themes received:', themes.length)
      console.log('Themes:', themes)

      if (!themes || themes.length === 0) {
        throw new Error(
          'No themes were generated - please try with different content',
        )
      }

      setSuggestedThemes(themes)
      setStep('themes')
    } catch (error) {
      console.error('=== ANALYSIS ERROR (file-based) ===')
      console.error('Error:', error)
      console.error('Message:', error.message)

      if (error.message?.includes('API key not configured')) {
        setError(
          <div className="space-y-3">
            <p>OpenAI API key not configured in edge functions.</p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              <p className="font-semibold text-blue-900 mb-2">
                To fix this:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-blue-800">
                <li>
                  Get an API key from{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-blue-600"
                  >
                    OpenAI Dashboard
                  </a>
                </li>
                <li>Go to your Supabase project dashboard</li>
                <li>Navigate to Settings → Edge Functions</li>
                <li>
                  Add secret:{' '}
                  <code className="bg-blue-100 px-1 rounded">
                    OPENAI_API_KEY_ITL
                  </code>
                </li>
                <li>Redeploy the edge functions</li>
              </ol>
            </div>
          </div>,
        )
      } else {
        setError(
          error.message || 'Failed to analyze stimulus. Please try again.',
        )
      }
    } finally {
      setAnalyzing(false)
    }

    // Important: don’t fall through to text-path if we just tried file-path
    return
  }

  // 🔹 Path 2: file mode selected but we *don’t* have extracted text yet
  // (e.g. they uploaded a file but never clicked "Save & extract")
  const isFileMode = stimulusMode === 'file'
  const hasUploadedFile = Boolean(uploadedFile)

  if (isFileMode && hasUploadedFile && !hasExtractedText && !savedStimulus) {
    setError(
      'Please click "Save & extract content" first so we can read your file.',
    )
    return
  }

  // 🔹 Path 3: pasted-text-only analysis (existing behaviour)
  if (!formData.content.trim()) {
    setError(
      'Please paste at least a short excerpt or summary from your stimulus so we can analyse it.',
    )
    return
  }

  if (formData.content.trim().length < 50) {
    setError(
      'Please provide more substantial content (at least 50 characters) for meaningful analysis.',
    )
    return
  }

  setAnalyzing(true)
  try {
    console.log('=== STARTING AI ANALYSIS (raw content) ===')
    console.log('Subject:', formData.subject)
    console.log('Content length:', formData.content.length)
    console.log(
      'Content preview:',
      formData.content.substring(0, 100) + '...',
    )

    const themes = await aiService.analyzeStimulus(
      formData.content,
      formData.subject,
    )

    console.log('=== ANALYSIS COMPLETE ===')
    console.log('Number of themes received:', themes.length)
    console.log('Themes:', themes)

    if (!themes || themes.length === 0) {
      throw new Error(
        'No themes were generated - please try with different content',
      )
    }

    setSuggestedThemes(themes)
    setStep('themes')
  } catch (error) {
    console.error('=== ANALYSIS ERROR (text-based) ===')
    console.error('Error:', error)
    console.error('Message:', error.message)

    if (error.message?.includes('API key not configured')) {
      setError(
        <div className="space-y-3">
          <p>OpenAI API key not configured in edge functions.</p>
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
            <p className="font-semibold text-blue-900 mb-2">
              To fix this:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-blue-800">
              <li>
                Get an API key from{' '}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-600"
                >
                  OpenAI Dashboard
                </a>
              </li>
              <li>Go to your Supabase project dashboard</li>
              <li>Navigate to Settings → Edge Functions</li>
              <li>
                Add secret:{' '}
                <code className="bg-blue-100 px-1 rounded">
                  OPENAI_API_KEY_ITL
                </code>
              </li>
              <li>Redeploy the edge functions</li>
            </ol>
          </div>
        </div>,
      )
    } else {
      setError(
        error.message || 'Failed to analyze stimulus. Please try again.',
      )
    }
  } finally {
    setAnalyzing(false)
  }
}

  const handleThemeToggle = (theme) => {
    setSelectedThemes((prev) => {
      const exists = prev.find((t) => t.label === theme.label)
      if (exists) {
        return prev.filter((t) => t.label !== theme.label)
      } else {
        return [...prev, theme]
      }
    })
  }

  const handleComplete = async () => {
    if (selectedThemes.length === 0) {
      setError('Please select at least one theme.')
      return
    }

    setLoading(true)
    setError('')
    try {
      // Ensure we have a stimulus (reusing any saved/reused one)
      const stimulus = await createOrUpdateStimulus()

      // 3. Create stimulus themes
      const themeInserts = selectedThemes.map((theme) => ({
        stimulus_id: stimulus.id,
        label: theme.label,
        description: theme.description,
      }))
      const { data: themes, error: themesError } = await supabase
        .from('stimulus_themes')
        .insert(themeInserts)
        .select()

      if (themesError) throw themesError

            // 4. Create lesson design
      const primaryThemeLabel = selectedThemes[0]?.label || null

      const autoTitle =
        primaryThemeLabel && formData.subject
          ? `${formData.subject} – ${primaryThemeLabel}`
          : primaryThemeLabel || formData.subject || 'New lesson design'

      const { data: lessonDesign, error: lessonError } = await supabase
        .from('lesson_designs')
        .insert({
          stimulus_id: stimulus.id,
          subject: formData.subject,
          title: autoTitle,              // 👈 NEW
          status: 'collecting_input',
        })
        .select()
        .single()

      if (lessonError) throw lessonError

      // 5. Link themes to lesson design
      const themeLinks = themes.map((theme) => ({
        lesson_design_id: lessonDesign.id,
        stimulus_theme_id: theme.id,
      }))
      const { error: linksError } = await supabase
        .from('lesson_design_themes')
        .insert(themeLinks)

      if (linksError) throw linksError

      onComplete({ lessonDesign, stimulus, themes: selectedThemes })
    } catch (error) {
      console.error('Error creating lesson design:', error)
      setError('Failed to create lesson design. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (step === 'themes') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Select Learning Themes
          </h2>
          <p className="text-gray-600 mt-2">
            Based on your stimulus content, here are suggested themes for your
            lesson. Select the ones you&apos;d like to focus on.
          </p>
        </div>

        {suggestedThemes.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon
              icon={FiAlertTriangle}
              className="w-12 h-12 text-yellow-500 mx-auto mb-4"
            />
            <p className="text-gray-500 mb-4">No themes were generated</p>
            <button
              onClick={() => setStep('input')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back to Input
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {suggestedThemes.map((theme, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedThemes.find((t) => t.label === theme.label)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleThemeToggle(theme)}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={`mt-1 w-4 h-4 rounded border flex items-center justify-center ${
                        selectedThemes.find((t) => t.label === theme.label)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedThemes.find(
                        (t) => t.label === theme.label,
                      ) && (
                        <SafeIcon
                          icon={FiCheck}
                          className="w-3 h-3 text-white"
                        />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {theme.label}
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        {theme.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-md">
                {typeof error === 'string' ? error : error}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => setStep('input')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={loading || selectedThemes.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Continue with Selected Themes'}
              </button>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Create New Lesson Design
        </h2>
        <p className="text-gray-600 mt-2">
          Start by selecting your subject and providing your stimulus, either by
          pasting text or uploading a file.
        </p>
      </div>

      <form onSubmit={handleAnalyzeStimulus} className="space-y-6">
        {/* Subject */}
        <div>
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Subject *
          </label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a subject</option>
            <option value="English">English</option>
            <option value="Music">Music</option>
            <option value="Art">Art</option>
            <option value="History">History</option>
            <option value="Science">Science</option>
            <option value="Mathematics">Mathematics</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Two-column stimulus modes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Paste text mode */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-800">
                Paste stimulus text
              </h3>
              <button
                type="button"
                onClick={() => setStimulusMode('text')}
                className={`text-xs px-2 py-1 rounded-full border ${
                  stimulusMode === 'text'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
              >
                Active
              </button>
            </div>
            <textarea
              id="content"
              name="content"
              rows={12}
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Paste your stimulus content here (article, interview, text, etc.).&#10;&#10;If you upload a file, you can still paste a key excerpt or summary here so the AI can analyse it."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              Provide substantial content (at least 50 characters) for better
              analysis.
            </p>
          </div>

          {/* Upload file mode */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-800">
                Upload stimulus file (PDF or image)
              </h3>
              <button
                type="button"
                onClick={() => setStimulusMode('file')}
                className={`text-xs px-2 py-1 rounded-full border ${
                  stimulusMode === 'file'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
              >
                Active
              </button>
            </div>

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative flex flex-col items-center justify-center px-4 py-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400'
              }`}
              onClick={() => {
                // trigger hidden file input
                const input = document.getElementById(
                  'stimulus-file-input',
                )
                if (input) input.click()
              }}
            >
              <input
                id="stimulus-file-input"
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
              <SafeIcon
                icon={FiUpload}
                className="w-8 h-8 text-gray-400 mb-2"
              />
              <p className="text-sm text-gray-700">
                Drag and drop a PDF or image here, or click to browse.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                The file will be stored securely in your Supabase{' '}
                <code className="bg-gray-100 px-1 rounded">stimulus</code>{' '}
                bucket.
              </p>

              {uploadedFile && (
                <div className="mt-4 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2 w-full text-left">
                  <div className="font-medium truncate">
                    Selected file: {uploadedFile.name}
                  </div>
                  <div className="text-gray-500 mt-1">
                    Type: {uploadedFile.type || 'Unknown'} • Size:{' '}
                    {Math.round(uploadedFile.size / 1024)} KB
                  </div>
                </div>
              )}

              {/* Optional: show a note when reusing an existing file-based stimulus */}
              {savedStimulus &&
                savedStimulus.source_type === 'uploaded_file' &&
                !uploadedFile && (
                  <div className="mt-4 text-xs text-gray-600 bg-gray-50 border border-dashed border-gray-300 rounded px-3 py-2 w-full text-left">
                    Using existing uploaded stimulus file (id:{' '}
                    {savedStimulus.id}). You don&apos;t need to re-upload it.
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Save & extract button + extracted_text preview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleSaveAndExtract}
              disabled={
                savingAndExtracting ||
                !formData.subject.trim() ||
                (!savedStimulus &&
                  !uploadedFile &&
                  !formData.content.trim())
              }
              className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingAndExtracting ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                  <span>Saving &amp; extracting content...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  <span>Save &amp; extract content</span>
                </>
              )}
            </button>
            {savedStimulus && (
              <span className="text-xs text-gray-500">
                Stimulus saved (id: {savedStimulus.id})
              </span>
            )}
          </div>

          {extractedText && (
            <div className="mt-2">
              <h4 className="text-sm font-medium text-gray-800">
                Extracted text (debug)
              </h4>
              <p className="text-xs text-gray-500 mb-1">
                This is the text that will be used for theme analysis when you
                analyse a file-based stimulus.
              </p>
              <div className="max-h-48 overflow-auto text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 whitespace-pre-wrap">
                {extractedText}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
            {typeof error === 'string' ? error : error}
          </div>
        )}

        <button
          type="submit"
          disabled={analyzing || !formData.subject.trim()}
          className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {analyzing ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              <span>Analyzing stimulus content...</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span>Analyze Stimulus &amp; Continue</span>
            </>
          )}
        </button>
      </form>
    </div>
  )
}

export default StimulusStep